import { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  IconButton,
  useTheme,
  Avatar,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { tokens } from "../../theme";

// Icons import..
import {
  HomeOutlined as HomeOutlinedIcon,
  MenuOutlined as MenuOutlinedIcon,
  Person as PersonIcon,
  GroupOutlined as GroupOutlinedIcon,
  BadgeOutlined as BadgeOutlinedIcon,
  KeyOutlined as KeyOutlinedIcon,
  AccountBalanceWalletOutlined as AccountBalanceWalletOutlinedIcon,
  DirectionsCarFilledOutlined as DirectionsCarFilledOutlinedIcon,
  BuildCircleOutlined as BuildCircleOutlinedIcon,
  AccountBalanceOutlined as AccountBalanceOutlinedIcon,
  ShoppingCartOutlined as ShoppingCartOutlinedIcon,
  PointOfSaleOutlined as PointOfSaleOutlinedIcon,
  GroupsOutlined as GroupsOutlinedIcon,
  HandshakeOutlined as HandshakeOutlinedIcon,
  CategoryOutlined as CategoryOutlinedIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
  AssignmentReturnOutlined as AssignmentReturnOutlinedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  WarehouseOutlined as WarehouseOutlinedIcon,
  StorefrontOutlined as StorefrontOutlinedIcon,
  AddCircleOutlineOutlined as AddCircleOutlineOutlinedIcon,
  ListAltOutlined as ListAltOutlinedIcon,
  Dashboard as DashboardIcon,
} from "@mui/icons-material";
import { useMediaQuery } from "@mui/material";

const drawerWidth = 260;
const collapsedWidth = 80;

const Sidebar = ({ isSidebar, setIsSidebar }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [selected, setSelected] = useState("");
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [openSubmenus, setOpenSubmenus] = useState({});

  const authType = localStorage.getItem("authType");

  const user =
    authType === "company"
      ? JSON.parse(localStorage.getItem("company"))
      : JSON.parse(localStorage.getItem("user"));

  const isSales = user?.roleName?.toLowerCase() === "sales";
  // Check if dark mode
  const isDarkMode = theme.palette.mode === "dark";

  useEffect(() => {
    const authType = localStorage.getItem("authType");

    // COMPANY = SUPER ADMIN
    if (authType === "company") {
      const allModules = [
        "Dashboard",
        "Contacts Information",
        "Profile Form",
        "User",
        "Role",
        "Permission",
        "Warehouse",
        "Customer",
        "Category",
        "Item",
        // "Balance",
        // "Balance Sheet",
        // "Expence",
        // "Car Information",
        // "Car Operation",
        "Store",
        "Purchase",
        "Sales",
        // "Lending",
        "Return",
        "Stock Out",
        "Expense Report",
        "Sales Report",
        "Purchase Report",
        "Lending Report",
        "Return Report",
        "Stockout Report",
        "Income Report",
        "Profit Report",
        // "FS",
        // "Z Balance",
      ];

      const allActions = ["create", "read", "update", "delete"];

      const fullPermissions = {};

      allModules.forEach((m) => {
        fullPermissions[m] = allActions;
      });

      setPermissions(fullPermissions);

      localStorage.setItem("permissions", JSON.stringify(fullPermissions));

      return;
    }

    // NORMAL USER LOGIN
    const user = JSON.parse(localStorage.getItem("user"));

    if (!user?.roleId) return;

    axios
      .get(
        `${process.env.REACT_APP_API_URL}/api/permission/role/${user.roleId}`,
      )
      .then((res) => {
        const perms = {};

        (res.data || []).forEach((p) => {
          perms[p.module] = p.actions;
        });

        setPermissions(perms);

        localStorage.setItem("permissions", JSON.stringify(perms));
      });
  }, []);

  useEffect(() => {
    const currentPath = location.pathname;

    const modulePaths = [
      "/user",
      "/role",
      "/permission",
      // "/expence",
      // "/carinformation",
      // "/caroperation",
      // "/balance",
      // "/balanceSheet",
      // "/fs",
      "/purchase",
      "/sales",
      "/customer",
      // "/lending",
      "/category",
      "/item",
      "/return",
      "/stockout",
      "/warehouse",
      "/store",
      // "/z balance",
      "/reports",
      "/sales-dashboard",
    ];

    const newOpenSubmenus = {};

    modulePaths.forEach((path) => {
      if (currentPath.includes(path)) {
        const moduleName = path.replace("/", "").replace(" ", "");
        newOpenSubmenus[moduleName] = true;
      }
    });

    if (currentPath.includes("/reports")) {
      newOpenSubmenus["Reports"] = true;
    }

    setOpenSubmenus((prev) => ({ ...prev, ...newOpenSubmenus }));
  }, [location.pathname]);

  const isActive = (path, title) => {
    return location.pathname === path || selected === title;
  };

  const toggleSubmenu = (label) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  // Get hover/active color based on theme
  const getHoverActiveColor = () => {
    return isDarkMode ? colors.primary[300] : colors.greenAccent[800];
  };

  // Get text color based on theme and state
  const getTextColor = (isActiveOrOpen = false) => {
    if (isDarkMode) {
      return "#ffffff"; // White text in dark mode
    }
    return isActiveOrOpen ? "#000000" : colors.grey[100]; // Black when active in light mode
  };

  // Single item component
  const Item = ({ title, to, icon }) => {
    const active = isActive(to, title);
    const hoverActiveColor = getHoverActiveColor();
    const textColor = getTextColor(active);

    return (
      <ListItem disablePadding sx={{ display: "block", mb: 0.5 }}>
        <ListItemButton
          component={Link}
          to={to}
          onClick={() => {
            setSelected(title);

            if (isMobile) {
              setIsSidebar(false);
            }
          }}
          sx={{
            minHeight: 40,
            justifyContent: isCollapsed ? "center" : "initial",
            px: isCollapsed ? 0 : 3.5,
            py: 1,
            backgroundColor: active ? hoverActiveColor : "transparent",
            borderRight: active ? `3px solid ${hoverActiveColor}` : "none",
            "&:hover": {
              backgroundColor: hoverActiveColor + " !important",
              color: textColor + " !important",
              "& .MuiListItemIcon-root": {
                color: textColor + " !important",
              },
            },
            color: textColor,
            borderRadius: 0,
            mx: 0,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: isCollapsed ? 0 : 2,
              justifyContent: "center",
              color: textColor,
            }}
          >
            {icon}
          </ListItemIcon>
          {!isCollapsed && (
            <ListItemText
              primary={title}
              primaryTypographyProps={{
                fontSize: "14px",
                fontWeight: 500,
              }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  // CRUD Group component
  const CRUDGroup = ({ label, basePath, icon: Icon }) => {
    const actions = permissions[label];
    const normalizedLabel = label.replace(" ", "");

    const isOpen = openSubmenus[normalizedLabel] || false;

    if (!actions) return null;

    const handleToggle = (e) => {
      e.preventDefault();
      toggleSubmenu(normalizedLabel);
    };

    const isChildActive = (path, title) => {
      return location.pathname === path || selected === title;
    };

    const hoverActiveColor = getHoverActiveColor();
    const parentTextColor = getTextColor(isOpen);
    const childTextColor = (childActive) => getTextColor(childActive);

    return (
      <>
        <ListItem disablePadding sx={{ display: "block", mb: 0.5 }}>
          <ListItemButton
            onClick={handleToggle}
            sx={{
              minHeight: 40,
              justifyContent: isCollapsed ? "center" : "initial",
              px: isCollapsed ? 0 : 3.5,
              py: 1,
              backgroundColor: isOpen ? hoverActiveColor : "transparent",
              borderRight: isOpen ? `3px solid ${hoverActiveColor}` : "none",
              "&:hover": {
                backgroundColor: hoverActiveColor + " !important",
                color: parentTextColor + " !important",
                "& .MuiListItemIcon-root": {
                  color: parentTextColor + " !important",
                },
              },
              color: parentTextColor,
              borderRadius: 0,
              mx: 0,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: isCollapsed ? 0 : 2,
                justifyContent: "center",
                color: parentTextColor,
              }}
            >
              <Icon />
            </ListItemIcon>
            {!isCollapsed && (
              <>
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                />
                <Box
                  sx={{
                    ml: 1,
                    color: parentTextColor,
                    fontSize: "14px",
                  }}
                >
                  {isOpen ? "▾" : "▸"}
                </Box>
              </>
            )}
          </ListItemButton>
        </ListItem>

        {!isCollapsed && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {actions.includes("create") && (
                <ListItem disablePadding sx={{ display: "block", mb: 0.5 }}>
                  <ListItemButton
                    component={Link}
                    to={`${basePath}/add`}
                    onClick={() => {
                      setSelected(`Add- ${label}`);

                      if (isMobile) {
                        setIsSidebar(false);
                      }
                    }}
                    sx={{
                      minHeight: 36,
                      pl: 6,
                      py: 0.75,
                      backgroundColor: isChildActive(
                        `${basePath}/add`,
                        `Add- ${label}`,
                      )
                        ? hoverActiveColor
                        : "transparent",
                      "&:hover": {
                        backgroundColor: hoverActiveColor + " !important",
                        color: childTextColor(true) + " !important",
                        "& .MuiListItemIcon-root": {
                          color: childTextColor(true) + " !important",
                        },
                      },
                      color: childTextColor(
                        isChildActive(`${basePath}/add`, `Add- ${label}`),
                      ),
                      borderRadius: 0,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 30,
                        mr: 1.5,
                        color: childTextColor(
                          isChildActive(`${basePath}/add`, `Add- ${label}`),
                        ),
                      }}
                    >
                      <AddCircleOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={`Add- ${label}`}
                      primaryTypographyProps={{
                        fontSize: "13px",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )}
              {actions.includes("read") && (
                <ListItem disablePadding sx={{ display: "block", mb: 0.5 }}>
                  <ListItemButton
                    component={Link}
                    to={`${basePath}/view`}
                    onClick={() => {
                      setSelected(`View- ${label}`);

                      if (isMobile) {
                        setIsSidebar(false);
                      }
                    }}
                    sx={{
                      minHeight: 36,
                      pl: 6,
                      py: 0.75,
                      backgroundColor: isChildActive(
                        `${basePath}/view`,
                        `View- ${label}`,
                      )
                        ? hoverActiveColor
                        : "transparent",
                      "&:hover": {
                        backgroundColor: hoverActiveColor + " !important",
                        color: childTextColor(true) + " !important",
                        "& .MuiListItemIcon-root": {
                          color: childTextColor(true) + " !important",
                        },
                      },
                      color: childTextColor(
                        isChildActive(`${basePath}/view`, `View- ${label}`),
                      ),
                      borderRadius: 0,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 30,
                        mr: 1.5,
                        color: childTextColor(
                          isChildActive(`${basePath}/view`, `View- ${label}`),
                        ),
                      }}
                    >
                      <ListAltOutlinedIcon sx={{ fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={`View- ${label}`}
                      primaryTypographyProps={{
                        fontSize: "13px",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )}
            </List>
          </Collapse>
        )}
      </>
    );
  };

  // Replace the entire ReportsMenu component with this:

  // Reports Submenu
  const ReportsMenu = () => {
    // Check if user has any report permissions
    const hasAnyReportPermission = [
      "Expense Report",
      "Sales Report",
      "Purchase Report",
      "Lending Report",
      "Return Report",
      "Stockout Report",
      "Income Report",
      "Profit Report",
    ].some((report) => permissions[report]?.includes("read"));

    if (!hasAnyReportPermission) return null;

    const isOpen = openSubmenus["Reports"] || false;

    const handleToggle = (e) => {
      e.preventDefault();
      toggleSubmenu("Reports");
    };

    const hoverActiveColor = getHoverActiveColor();
    const parentTextColor = getTextColor(isOpen);

    return (
      <>
        <ListItem disablePadding sx={{ display: "block", mb: 0.5 }}>
          <ListItemButton
            onClick={handleToggle}
            sx={{
              minHeight: 40,
              justifyContent: isCollapsed ? "center" : "initial",
              px: isCollapsed ? 0 : 3.5,
              py: 1,
              backgroundColor: isOpen ? hoverActiveColor : "transparent",
              borderRight: isOpen ? `3px solid ${hoverActiveColor}` : "none",
              "&:hover": {
                backgroundColor: hoverActiveColor + " !important",
                color: parentTextColor + " !important",
                "& .MuiListItemIcon-root": {
                  color: parentTextColor + " !important",
                },
              },
              color: parentTextColor,
              borderRadius: 0,
              mx: 0,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: isCollapsed ? 0 : 2,
                justifyContent: "center",
                color: parentTextColor,
              }}
            >
              <AccountBalanceWalletOutlinedIcon />
            </ListItemIcon>
            {!isCollapsed && (
              <>
                <ListItemText
                  primary="Reports"
                  primaryTypographyProps={{
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                />
                <Box
                  sx={{
                    ml: 1,
                    color: parentTextColor,
                    fontSize: "14px",
                  }}
                >
                  {isOpen ? "▾" : "▸"}
                </Box>
              </>
            )}
          </ListItemButton>
        </ListItem>

        {!isCollapsed && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {[
                {
                  title: "Expense Report",
                  path: "/reports/expense",
                  module: "Expense Report",
                },
                {
                  title: "Sales Report",
                  path: "/reports/sales",
                  module: "Sales Report",
                },
                {
                  title: "Purchase Report",
                  path: "/reports/purchase",
                  module: "Purchase Report",
                },
                {
                  title: "Lending Report",
                  path: "/reports/lending",
                  module: "Lending Report",
                },
                {
                  title: "Return Report",
                  path: "/reports/return",
                  module: "Return Report",
                },
                {
                  title: "Stockout Report",
                  path: "/reports/stockout",
                  module: "Stockout Report",
                },
                {
                  title: "Income Report",
                  path: "/reports/income",
                  module: "Income Report",
                },
                {
                  title: "Profit Report",
                  path: "/reports/profit",
                  module: "Profit Report",
                },
              ].map((report) => {
                // Only show report if user has read permission for it
                if (!permissions[report.module]?.includes("read")) return null;

                const active = isActive(report.path, report.title);
                const textColor = getTextColor(active);

                return (
                  <ListItem
                    key={report.path}
                    disablePadding
                    sx={{ display: "block", mb: 0.5 }}
                  >
                    <ListItemButton
                      component={Link}
                      to={report.path}
                      onClick={() => setSelected(report.title)}
                      sx={{
                        minHeight: 36,
                        pl: 6,
                        py: 0.75,
                        backgroundColor: active
                          ? hoverActiveColor
                          : "transparent",
                        "&:hover": {
                          backgroundColor: hoverActiveColor + " !important",
                          color: textColor + " !important",
                          "& .MuiListItemIcon-root": {
                            color: textColor + " !important",
                          },
                        },
                        color: textColor,
                        borderRadius: 0,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 30,
                          mr: 1.5,
                          color: textColor,
                        }}
                      >
                        <ListAltOutlinedIcon sx={{ fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={report.title}
                        primaryTypographyProps={{
                          fontSize: "13px",
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
        )}
      </>
    );
  };

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? isSidebar : true}
      onClose={() => setIsSidebar(false)}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        width: isCollapsed ? collapsedWidth : drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: isCollapsed ? collapsedWidth : drawerWidth,
          boxSizing: "border-box",
          backgroundColor: colors.primary[400],
          color: colors.grey[100],
          borderRight: "none",
          overflowX: "hidden",
        },
      }}
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: isCollapsed ? "center" : "space-between",
          alignItems: "center",
        }}
      >
        {!isCollapsed && <Box sx={{ flex: 1 }} />}
        <IconButton
          onClick={() => setIsCollapsed(!isCollapsed)}
          sx={{
            color: isDarkMode ? "#ffffff" : colors.grey[100],
            "&:hover": {
              backgroundColor: getHoverActiveColor() + " !important",
              color: isDarkMode ? "#ffffff" : "#000000" + " !important",
            },
          }}
        >
          <MenuOutlinedIcon />
        </IconButton>
      </Box>

      {!isCollapsed && (
        <Box sx={{ px: 3, pb: 3, textAlign: "center" }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              mx: "auto",
              mb: 2,
              bgcolor: colors.grey[800],
            }}
          >
            <PersonIcon sx={{ fontSize: 50, color: colors.greenAccent[500] }} />
          </Avatar>
          <Typography
            variant="h5"
            color={isDarkMode ? "#ffffff" : colors.grey[100]}
            fontWeight="bold"
            sx={{ m: "10px 0 0 0" }}
          >
            {user?.fullName || "Manager"}
          </Typography>
          <Typography variant="h6" color={colors.greenAccent[500]}>
            {user?.roleName || "Role"}
          </Typography>
        </Box>
      )}

      <Box sx={{ px: isCollapsed ? 0 : 2 }}>
        {permissions["Dashboard"]?.includes("read") && (
          <Item title="Dashboard" to="/" icon={<HomeOutlinedIcon />} />
        )}

        {isSales && (
          <Item
            title="Sales Dashboard"
            to="/sales-dashboard"
            icon={<DashboardIcon />}
          />
        )}

        <ReportsMenu />

        <CRUDGroup label="User" basePath="/user" icon={GroupOutlinedIcon} />
        <CRUDGroup label="Role" basePath="/role" icon={BadgeOutlinedIcon} />
        <CRUDGroup
          label="Permission"
          basePath="/permission"
          icon={KeyOutlinedIcon}
        />
        <CRUDGroup
          label="Expence"
          basePath="/expence"
          icon={AccountBalanceWalletOutlinedIcon}
        />
        <CRUDGroup
          label="Car Information"
          basePath="/carinformation"
          icon={DirectionsCarFilledOutlinedIcon}
        />
        <CRUDGroup
          label="Car Operation"
          basePath="/caroperation"
          icon={BuildCircleOutlinedIcon}
        />

        <CRUDGroup
          label="Balance Sheet"
          basePath="/balanceSheet"
          icon={AccountBalanceOutlinedIcon}
        />
        <CRUDGroup label="FS" basePath="/fs" icon={PointOfSaleOutlinedIcon} />
        <CRUDGroup
          label="Purchase"
          basePath="/purchase"
          icon={ShoppingCartOutlinedIcon}
        />
        <CRUDGroup
          label="Sales"
          basePath="/sales"
          icon={PointOfSaleOutlinedIcon}
        />
        <CRUDGroup
          label="Customer"
          basePath="/customer"
          icon={GroupsOutlinedIcon}
        />
        <CRUDGroup
          label="Lending"
          basePath="/lending"
          icon={HandshakeOutlinedIcon}
        />
        <CRUDGroup
          label="Category"
          basePath="/category"
          icon={CategoryOutlinedIcon}
        />
        <CRUDGroup
          label="Item"
          basePath="/item"
          icon={Inventory2OutlinedIcon}
        />
        <CRUDGroup
          label="Return"
          basePath="/return"
          icon={AssignmentReturnOutlinedIcon}
        />
        <CRUDGroup
          label="Stock Out"
          basePath="/stockout"
          icon={LocalShippingOutlinedIcon}
        />
        <CRUDGroup
          label="Warehouse"
          basePath="/warehouse"
          icon={WarehouseOutlinedIcon}
        />
        <CRUDGroup
          label="Store"
          basePath="/store"
          icon={StorefrontOutlinedIcon}
        />
        <CRUDGroup
          label="Z Balance"
          basePath="/z balance"
          icon={AccountBalanceOutlinedIcon}
        />
      </Box>
    </Drawer>
  );
};

export default Sidebar;
